import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Loader, AlertCircle, Check, ArrowRight,
  Camera, Image as ImageIcon, FileText, Sparkles, Lock,
} from 'lucide-react';
import { Button, Card, TabBar } from '../components/common';
import { useSubscription } from '../context/SubscriptionContext';
import { aiApi, extractedToRecipeForm } from '../services/ai';
import { fileToAiImageSource } from '../utils/imageProcessing';

const MODE_TABS = [
  { value: 'url', label: 'Link' },
  { value: 'photo', label: 'Photo' },
  { value: 'text', label: 'Text' },
];

export default function ImportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { promptUpgrade } = useSubscription();

  const [mode, setMode] = useState('url');

  // --- URL import (existing flow — behaviour unchanged) ---
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('idle');
  const [error] = useState('');

  // --- AI imports (photo / text) ---
  const [text, setText] = useState('');
  const [aiStatus, setAiStatus] = useState('idle'); // idle | preparing | loading | success
  const [aiError, setAiError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [usage, setUsage] = useState(null); // { used, limit } — limit null = unlimited
  const [fileName, setFileName] = useState('');

  const libraryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const processedLinkKeyRef = useRef(null);

  const handleImport = useCallback((value = url) => {
    if (!value.trim()) return;
    setStatus('loading');
    // Simulate URL parsing — backend integration is future work.
    setTimeout(() => {
      setStatus('success');
      setTimeout(() => {
        navigate('/submit', {
          state: {
            prefill: {
              title: 'Imported Recipe',
              sourceUrl: value,
              description: 'Recipe imported from URL. Edit the details below.',
            },
          },
        });
      }, 1000);
    }, 2000);
  }, [url, navigate]);

  const runExtract = useCallback(async (source) => {
    setAiStatus('loading');
    setAiError('');
    setLimitReached(false);
    try {
      const res = await aiApi.extractRecipe(source);
      if (res?.usage) setUsage(res.usage);
      setAiStatus('success');
      navigate('/submit', { state: { prefill: extractedToRecipeForm(res.recipe) } });
    } catch (err) {
      setAiStatus('idle');
      if (err?.code === 'limit_reached') {
        if (err.usage) setUsage(err.usage);
        setLimitReached(true);
      } else if (err?.code === 'no_recipe') {
        setAiError(err.message || "We couldn't find a recipe in that. Try a clearer photo or more complete text.");
      }
      // Transient and unexpected errors are already surfaced as a toast by the AI service.
    }
  }, [navigate]);

  const handleTextExtract = useCallback((value = text) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    runExtract({ type: 'text', text: trimmed });
  }, [text, runExtract]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setFileName(file.name || 'photo');
    setAiError('');
    setLimitReached(false);
    setAiStatus('preparing');
    let source;
    try {
      source = await fileToAiImageSource(file);
    } catch {
      setAiStatus('idle');
      setAiError("We couldn't read that image. Try a different photo or screenshot.");
      return;
    }
    await runExtract(source);
  };

  // Share-extension deep link handoff — auto-start the matching import.
  useEffect(() => {
    const deepLink = location.state?.deepLink;
    if (!deepLink || processedLinkKeyRef.current === location.key) return;
    processedLinkKeyRef.current = location.key;
    if (deepLink.type === 'url') {
      setMode('url');
      setUrl(deepLink.value);
      handleImport(deepLink.value);
    } else if (deepLink.type === 'text') {
      setMode('text');
      setText(deepLink.value);
      handleTextExtract(deepLink.value);
    }
  }, [location.key, location.state, handleImport, handleTextExtract]);

  const aiBusy = aiStatus === 'preparing' || aiStatus === 'loading';
  const remaining = usage && usage.limit != null ? Math.max(usage.limit - usage.used, 0) : null;

  const usageNote = remaining != null && !limitReached && (
    <p className="mt-3 text-xs text-gray-500 dark:text-night-muted">
      {remaining} of {usage.limit} free imports left this month
    </p>
  );

  const limitPanel = limitReached && (
    <div className="mt-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
      <div className="flex items-start gap-2">
        <Lock size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-night-text">
            You've used all {usage?.limit ?? 5} free imports this month
          </p>
          <p className="text-sm text-gray-600 dark:text-night-muted mt-0.5">
            Go Premium for unlimited photo and text imports.
          </p>
        </div>
      </div>
      <Button className="mt-3 w-full" onClick={() => promptUpgrade('ai_import')}>
        Upgrade to Premium
      </Button>
    </div>
  );

  const aiErrorNote = aiError && (
    <p className="mt-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
      <AlertCircle size={16} className="flex-shrink-0" aria-hidden="true" /> {aiError}
    </p>
  );

  const aiLoadingNote = aiBusy && (
    <p className="mt-4 text-sm text-gray-600 dark:text-night-muted flex items-center gap-2">
      <Loader size={16} className="animate-spin flex-shrink-0" aria-hidden="true" />
      {aiStatus === 'preparing' ? 'Preparing your photo…' : 'Reading your recipe…'}
    </p>
  );

  const aiSuccessNote = aiStatus === 'success' && (
    <p className="mt-4 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
      <Check size={20} aria-hidden="true" /> Recipe found! Redirecting to editor…
    </p>
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex w-16 h-16 items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mb-3" aria-hidden="true">
          <Sparkles size={32} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-night-text">Import a Recipe</h2>
        <p className="text-sm text-gray-500 dark:text-night-muted mt-1">
          Bring in recipes from a link, a photo or screenshot, or pasted text.
        </p>
      </div>

      <TabBar tabs={MODE_TABS} activeTab={mode} onChange={setMode} />

      {mode === 'url' && (
        <>
          <Card>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://www.example.com/recipe…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                disabled={status === 'loading'}
                className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              />
              <Button
                onClick={() => handleImport()}
                disabled={!url.trim() || status === 'loading'}
                className="min-h-0 py-2.5 px-3"
                aria-label="Import"
              >
                {status === 'loading' ? <Loader size={20} className="animate-spin" /> :
                 status === 'success' ? <Check size={20} /> :
                 <ArrowRight size={20} />}
              </Button>
            </div>

            {error && (
              <p className="mt-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                <AlertCircle size={16} aria-hidden="true" /> {error}
              </p>
            )}

            {status === 'loading' && (
              <ul className="mt-4 space-y-1.5 text-sm text-gray-600 dark:text-night-muted">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />Fetching recipe page…</li>
                <li className="flex items-center gap-2 opacity-60"><span className="w-2 h-2 rounded-full bg-gray-300" />Extracting ingredients & steps…</li>
                <li className="flex items-center gap-2 opacity-60"><span className="w-2 h-2 rounded-full bg-gray-300" />Parsing nutritional data…</li>
              </ul>
            )}

            {status === 'success' && (
              <p className="mt-4 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                <Check size={20} aria-hidden="true" /> Recipe imported! Redirecting to editor…
              </p>
            )}
          </Card>

          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide mb-3">
              Supported Sites
            </h3>
            <p className="text-sm text-gray-700 dark:text-night-text mb-3">
              We support most recipe websites including AllRecipes, Food Network, BBC Good Food, Serious Eats, NYT Cooking, and thousands more.
            </p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-night-muted">
              <li className="flex items-center gap-2"><span aria-hidden="true">✨</span>Works best with sites using structured recipe data</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">🔒</span>Paywalled sites may require manual entry</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✏️</span>You can always edit imported recipes</li>
            </ul>
          </Card>
        </>
      )}

      {mode === 'photo' && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 dark:text-night-text">
            From a photo or screenshot
          </h3>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1 mb-4">
            Snap a cookbook page or handwritten recipe, or pick a screenshot — we'll read out the ingredients and steps for you.
          </p>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelected}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelected}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          />

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              onClick={() => cameraInputRef.current?.click()}
              disabled={aiBusy}
            >
              <Camera size={18} aria-hidden="true" /> Take a photo
            </Button>
            <Button
              onClick={() => libraryInputRef.current?.click()}
              disabled={aiBusy}
            >
              <ImageIcon size={18} aria-hidden="true" /> Choose image
            </Button>
          </div>

          {fileName && !aiBusy && aiStatus !== 'success' && !aiError && !limitReached && (
            <p className="mt-3 text-xs text-gray-500 dark:text-night-muted truncate">Last selected: {fileName}</p>
          )}

          {aiLoadingNote}
          {aiSuccessNote}
          {aiErrorNote}
          {limitPanel}
          {usageNote}
        </Card>
      )}

      {mode === 'text' && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 dark:text-night-text">
            Paste text or a caption
          </h3>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1 mb-4">
            Paste a TikTok or Instagram caption, a message from a friend, or any recipe text.
          </p>

          <label htmlFor="import-text" className="sr-only">Recipe text</label>
          <textarea
            id="import-text"
            rows={7}
            placeholder="Paste the recipe text here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={aiBusy}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 resize-y"
          />

          <Button
            onClick={() => handleTextExtract()}
            disabled={!text.trim() || aiBusy}
            className="mt-3 w-full"
          >
            {aiBusy
              ? <><Loader size={18} className="animate-spin" aria-hidden="true" /> Reading your recipe…</>
              : <><FileText size={18} aria-hidden="true" /> Extract recipe</>}
          </Button>

          {aiSuccessNote}
          {aiErrorNote}
          {limitPanel}
          {usageNote}
        </Card>
      )}

      <div className="text-center space-y-3">
        <p className="text-sm text-gray-500 dark:text-night-muted">Prefer to type it in yourself?</p>
        <Button variant="secondary" onClick={() => navigate('/submit')}>Add Recipe Manually</Button>
      </div>
    </div>
  );
}
